import { motion } from "framer-motion";

const TEAM_IMG = "https://images.pexels.com/photos/12903168/pexels-photo-12903168.jpeg";

const chapters = [
  {
    num: "01",
    title: "Rooted in Kilkenny",
    body: "From our office at 8 Ormonde Street, we serve the shopkeepers, founders, farmers and families of the south-east — and clients across Ireland. Local knowledge, national reach.",
  },
  {
    num: "02",
    title: "Numbers, honoured",
    body: "Every ledger tells a story. We keep yours immaculate — Revenue-ready filings, clean books and accounts you can actually read, delivered on time, every time.",
  },
  {
    num: "03",
    title: "Counsel, not just compliance",
    body: "Deadlines met are the floor, not the ceiling. We advise on cash flow, structure and growth — so your accounts become a tool for decisions, not a chore for Revenue.",
  },
];

const credentials = ["Chartered Accountants Ireland", "Registered Tax Agents", "Revenue ROS Partners"];

const fadeUp = {
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] },
};

export default function About() {
  return (
    <section id="about" data-testid="about-section" className="relative border-t hairline-gold py-24 lg:py-32">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 lg:grid-cols-12 lg:px-12">
        <div className="lg:col-span-5">
          <motion.div {...fadeUp} className="lg:sticky lg:top-32">
            <p className="overline-label mb-6 flex items-center gap-4" data-testid="about-overline">
              <span className="inline-block h-px w-10 bg-gold" />
              The Practice
            </p>
            <h2 className="font-serif text-4xl font-medium leading-[1.05] tracking-tight text-navy sm:text-5xl" data-testid="about-heading">
              A firm built on<br />trust, kept on<br /><em className="italic text-gold">principle.</em>
            </h2>
            <div className="relative mt-12">
              <div className="absolute -left-4 -top-4 h-full w-full border hairline-gold" aria-hidden="true" />
              <div className="relative aspect-[3/4] overflow-hidden">
                <img
                  src={TEAM_IMG}
                  alt="The AB & Co team collaborating in the office"
                  className="h-full w-full object-cover grayscale transition-all duration-700 hover:grayscale-0"
                  data-testid="about-team-image"
                />
                <div className="absolute inset-0 bg-navy/10" />
              </div>
            </div>
          </motion.div>
        </div>

        <div className="lg:col-span-6 lg:col-start-7">
          {chapters.map((c, i) => (
            <motion.div
              key={c.num}
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: i * 0.12 }}
              className="group border-b hairline-gold py-10 first:pt-0 lg:py-12"
              data-testid={`about-chapter-${c.num}`}
            >
              <div className="flex items-baseline gap-6">
                <span className="font-serif text-2xl italic text-gold">{c.num}.</span>
                <h3 className="font-serif text-2xl font-medium text-navy transition-transform duration-500 group-hover:translate-x-2 sm:text-3xl">
                  {c.title}
                </h3>
              </div>
              <p className="mt-4 max-w-lg pl-0 text-base leading-relaxed text-muted lg:pl-16">{c.body}</p>
            </motion.div>
          ))}

          <motion.div {...fadeUp} className="mt-12" data-testid="about-credentials">
            <p className="font-sans text-xs font-semibold uppercase tracking-[0.24em] text-navy/50">Credentials &amp; Memberships</p>
            <ul className="mt-5 flex flex-wrap gap-3">
              {credentials.map((c) => (
                <li
                  key={c}
                  className="border hairline-gold px-5 py-2.5 font-serif text-base italic text-navy/80 transition-colors duration-300 hover:border-gold"
                >
                  {c}
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
